// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// This function returns the status text corresponding to an orderStatus
/* eslint-disable import/prefer-default-export */
export const GetOrderStatus = (status: number) =>
  (status === 0 && 'Created') ||
  (status === 1 && 'Processing') ||
  (status === 2 && 'Autonomous successful') ||
  (status === 3 && 'Autonomous failed') ||
  (status === 4 && 'Manual drafting') ||
  (status === 5 && 'US QC') ||
  (status === 6 && 'Ready to Upload') ||
  (status === 7 && 'Done') ||
  (status === 8 && 'Canceled');

export type CustomerOrderStatus = 'Sourcing data' | 'Awaiting your data' | 'Data received' | 'Processing' | 'Completed' | 'Canceled' | 'Estimate';

// What "Data received" tells the customer on hover: more data is welcome, none is needed (Joel, 2026-10-16).
export const DATA_RECEIVED_NOTE = 'You can add more data until processing begins. We\'ll email you when it does.';

// The words a KML-only hold reads until Start Processing launches it (AUT-6266): "Sourcing data" while AirWorks
// sources the data; for a customer who chose to upload its own, "Awaiting your data", then "Data received" once a
// file of theirs has passed its EPSG check (the AUT-6266 check).
const kmlOnlyHoldStatus = (kmlOnlyOwnData?: boolean, ownDataReceived?: boolean): CustomerOrderStatus => {
  if (!kmlOnlyOwnData) return 'Sourcing data';
  return ownDataReceived ? 'Data received' : 'Awaiting your data';
};

// Customer-facing status words — the vocabulary the project sidebar badge has always used, shared with
// Order History now that in-flight orders list there (AUT-6526). GetOrderStatus above is the admin
// dropdown's internal vocabulary and stays off customer surfaces. Returns null when the order carries
// no state to name (an unplaced non-estimate, or a row from a server that sends no orderStatus) —
// callers render a dash, never a guessed state.
export const getCustomerOrderStatus = ({
  orderStatus,
  kmlOnly,
  kmlOnlyOwnData,
  kmlOnlyLaunched,
  ownDataReceived,
  isEstimate,
}: Pick<IOrder, 'orderStatus' | 'kmlOnly' | 'kmlOnlyOwnData' | 'kmlOnlyLaunched' | 'ownDataReceived' | 'isEstimate'>): CustomerOrderStatus | null => {
  if (orderStatus === 7) return 'Completed';
  if (orderStatus === 8) return 'Canceled';
  // A KML-only order's pipeline is deferred at placement and its clock hasn't started. Start Processing keeps it at
  // orderStatus 1 and marks the launch (kmlOnlyLaunched, from the order's launchedAt), so the hold's words give way
  // to "Processing" at the launch, not when the pipeline later moves the status on.
  if (kmlOnly && orderStatus === 1 && !kmlOnlyLaunched) return kmlOnlyHoldStatus(kmlOnlyOwnData, ownDataReceived);
  if (orderStatus !== undefined && orderStatus > 0 && orderStatus < 7) return 'Processing';
  if (isEstimate) return 'Estimate';
  return null;
};

// A KML-only hold waiting on the customer's own upload rather than on data AirWorks sources (the AUT-6266 check).
export const isOwnDataHold = (order?: Pick<IOrder, 'kmlOnly' | 'kmlOnlyOwnData'> | null): boolean => !!(order?.kmlOnly && order?.kmlOnlyOwnData);
